import type { IDBPDatabase } from "idb";
import type { MonologDB, StoredAudioFile } from "../indexeddb-schema.js";

type WithDbOperation = <T>(
	onUnavailableMessage: string,
	fallback: () => T | Promise<T>,
	operation: (db: IDBPDatabase<MonologDB>) => Promise<T>,
) => Promise<T>;

interface AudioStorageStats {
	totalFiles: number;
	totalSize: number;
	oldestFile: Date | null;
	newestFile: Date | null;
}

export class AudioRepository {
	constructor(private readonly withDb: WithDbOperation) {}

	async saveAudioFile(audioFile: StoredAudioFile): Promise<void> {
		return this.withDb(
			"Database not available, skipping audio file save",
			() => undefined,
			async (db) => {
				await db.put("audioFiles", audioFile);
			},
		);
	}

	async getAudioFile(fileId: string): Promise<StoredAudioFile | undefined> {
		return this.withDb(
			"Database not available, returning undefined for audio file",
			() => undefined,
			async (db) => db.get("audioFiles", fileId),
		);
	}

	async getAudioFilesBySession(sessionId: string): Promise<StoredAudioFile[]> {
		return this.withDb(
			"Database not available, returning empty audio files array",
			() => [],
			async (db) => db.getAllFromIndex("audioFiles", "by-session", sessionId),
		);
	}

	async deleteAudioFile(fileId: string): Promise<void> {
		return this.withDb(
			"Database not available, skipping audio file delete",
			() => undefined,
			async (db) => {
				await db.delete("audioFiles", fileId);
			},
		);
	}

	async getAllAudioFiles(): Promise<StoredAudioFile[]> {
		return this.withDb(
			"Database not available, returning empty array",
			() => [],
			async (db) => {
				const tx = db.transaction("audioFiles", "readonly");
				const store = tx.objectStore("audioFiles");
				const files: StoredAudioFile[] = [];
				let cursor = await store.openCursor();

				// Blob は大容量になり得るため、一括取得 API では除外してメモリ使用量を抑える
				while (cursor) {
					const { blob: _blob, ...metadataOnly } = cursor.value;
					files.push(metadataOnly);
					cursor = await cursor.continue();
				}

				await tx.done;
				return files;
			},
		);
	}

	async getAudioStorageStats(): Promise<AudioStorageStats> {
		return this.withDb(
			"Database not available, returning empty audio storage stats",
			() => ({
				totalFiles: 0,
				totalSize: 0,
				oldestFile: null,
				newestFile: null,
			}),
			async (db) => {
				const tx = db.transaction("audioFiles", "readonly");
				const store = tx.objectStore("audioFiles");
				const dateIndex = store.index("by-date");
				const [totalFiles, oldestCursor, newestCursor] = await Promise.all([
					store.count(),
					dateIndex.openCursor(null, "next"),
					dateIndex.openCursor(null, "prev"),
				]);

				let totalSize = 0;
				let cursor = await store.openCursor();

				while (cursor) {
					totalSize += cursor.value.size;
					cursor = await cursor.continue();
				}

				return {
					totalFiles,
					totalSize,
					oldestFile: oldestCursor ? oldestCursor.value.createdAt : null,
					newestFile: newestCursor ? newestCursor.value.createdAt : null,
				};
			},
		);
	}
}
